import React, { useEffect, useState } from "react";
import { accountAPI } from "services";
import { Loader, ProfilePhoto } from "components";
import useUniversalStore from "store/universalStore";
import { PhoneInput } from "react-international-phone";
import useOffcanvasStore from "store/offcanvasStore";
import { toast } from "sonner";
import { useForm, Controller, useWatch } from "react-hook-form";
import { defaultValues, validatePhone, validateAccountName } from "./config";
import "react-international-phone/style.css";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { getAllTagsFromAccounts } from "utils";
import { defaultTags } from "utils/tags";

const getNameRules = (accountId) => ({
  required: "Name is required",
  maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
  validate: (value) => validateAccountName(value, accountId)
});

const phoneRules = { validate: validatePhone };

const tagRules = {
  maxLength: { value: 20, message: "Tag cannot exceed 20 characters" }
};

const EditAccount = () => {
  const { accountId, write, accounts } = useUniversalStore((state) => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const [loading, setLoading] = useState(true);

  const { control, handleSubmit, setValue, register, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onTouched"
  });

  const photo = useWatch({ control, name: "photo" });

  const tags = getAllTagsFromAccounts(accounts);
  const mappedTags = defaultTags.concat(tags.map((tag) => ({ value: tag, label: tag })));

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        const account = await accountAPI.getAccountById(accountId);
        if (account) {
          setValue("name", account.get("name") || "");
          setValue("phone", account.get("phone") || "");
          setValue("address", account.get("address") || "");
          setValue("details", account.get("details") || "");
          setValue("tag", account.get("tag") || null);
          setValue("photo", {
            url: account.get("photo") || null,
            loading: false
          });
        }
      } catch (error) {
        toast.error("Failed to load account data");
      } finally {
        setLoading(false);
      }
    };
    fetchAccountData();
  }, [accountId, setValue]);

  const onSubmit = async (data) => {
    try {
      await accountAPI.updateAccount(
        accountId,
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value ?? data.tag ?? null,
        data.photo.url
      );

      write({ accounts: await accountAPI.getAccounts() });
      closeOffcanvas();
      toast.success("Account updated successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const inputClass = (name) => `form-control ${errors[name] ? "is-invalid" : ""}`;

  const getTagValue = (value) => {
    if (!value) return null;
    if (typeof value === "string") return { value, label: value };
    return value;
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="my-3" align="center">
        <ProfilePhoto
          key="edit-profile-photo"
          url={photo?.url}
          loading={photo?.loading}
          updateField={(field, data) => setValue(field, data)}
        />
      </div>

      <div className="mb-3">
        <div className="form-control border-0 p-0">
          <input
            {...register("name", getNameRules(accountId))}
            type="text"
            placeholder="Account Name"
            className={inputClass("name")}
            disabled={isSubmitting}
          />
        </div>
        {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}
      </div>

      <div className="mb-3">
        <div className="form-control border-0 p-0">
          <Controller
            name="phone"
            control={control}
            rules={phoneRules}
            render={({ field }) => (
              <PhoneInput
                {...field}
                defaultCountry="in"
                hideDropdown
                style={{ width: "100%" }}
                className={inputClass("phone")}
                disabled={isSubmitting}
              />
            )}
          />
        </div>
        {errors.phone && <div className="invalid-feedback d-block">{errors.phone.message}</div>}
      </div>

      <div className="mb-3">
        <Controller
          name="tag"
          control={control}
          rules={tagRules}
          render={({ field: { value, onChange } }) => (
            <Creatable
              isClearable
              options={mappedTags}
              value={getTagValue(value)}
              onChange={onChange}
              placeholder="Select or create a Tag"
              classNamePrefix="rs"
              classNames={creatableStyle}
              className={errors.tag ? "is-invalid" : value ? "is-valid" : ""}
              disabled={isSubmitting}
            />
          )}
        />
        {errors.tag && <div className="invalid-feedback d-block">{errors.tag.message}</div>}
      </div>

      <div className="mb-3">
        <div className="form-control border-0 p-0">
          <input
            {...register("address")}
            type="text"
            placeholder="Address"
            className={inputClass("address")}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="form-control border-0 p-0">
          <textarea
            {...register("details")}
            placeholder="Other Details"
            className={inputClass("details")}
            rows={3}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="px-3 position-absolute bottom-0 start-0 end-0 mb-3">
        <button
          type="submit"
          className="btn btn-app w-100 py-3 fs-5 fw-bold text-uppercase"
          disabled={isSubmitting || photo?.loading || !isValid}
        >
          {photo?.loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : isSubmitting ? "Updating..." : "Update"}
        </button>
      </div>
    </form>
  );
};

export default EditAccount;
